/* Microphone engine: live pitch frames, note events and chord (chroma) frames.
   Audio never leaves the browser: frames are analysed in memory and discarded. */
import { getBusyUntil, resume } from '../audio/engine';
import { type ChordMatch, type ChordRef, chroma, matchChord } from '../dsp/chord';
import { freqToMidi, hfLevel, rms } from '../dsp/math';
import { type NoteEvent, NoteTracker, type TrackerMode, type TrackerOptions } from '../dsp/noteTracker';
import { detectPitch } from '../dsp/pitch';
import { Emitter } from '../events';
import { award } from '../store/gamification';
import { getSettings, setSetting } from '../store/state';
import { baseGate, NoiseFloor } from './floor';

export type MicState = 'off' | 'starting' | 'on' | 'unavailable' | 'denied' | 'nodevice' | 'error';

export interface MicFrame {
  /** AudioContext time in seconds */
  t: number;
  rms: number;
  /** Hz, 0 when unvoiced */
  freq: number;
  clarity: number;
  /** pluck detector level (see hfLevel) */
  hf: number;
  /** true while the app itself is playing sound through speakers */
  gated: boolean;
  /** fractional MIDI note, null when unvoiced */
  midi: number | null;
  /** current level threshold (noise floor and sensitivity) */
  gate: number;
  /** estimated room noise level (RMS) */
  floor: number;
  /** a clear, loud enough pitch outside the gated period */
  voiced: boolean;
  /** Latest raw samples from the analyser. Only valid during the callback; do not keep. */
  buf: Float32Array;
  sr: number;
}

export interface ChordFrame {
  t: number;
  chroma: Float64Array;
  bass: number;
  rms: number;
  gated: boolean;
  active: boolean;
}

interface MicEvents {
  frame: MicFrame;
  chord: ChordFrame;
  state: MicState;
}

const events = new Emitter<MicEvents>();
let state: MicState = 'off';
let error: Error | null = null;
let level = 0;
/** RMS of the last frame heard while the app itself was silent (trackNotes compares with it). */
let openLevel = 0;
const floor = new NoiseFloor();
let stream: MediaStream | null = null;
let src: MediaStreamAudioSourceNode | null = null;
let analyser: AnalyserNode | null = null;
let sink: GainNode | null = null;
let buf: Float32Array<ArrayBuffer> = new Float32Array(0);
let raf = 0;
let lastT = 0;
let lastChroma = 0;

function setState(s: MicState, err?: unknown): void {
  state = s;
  error = err instanceof Error ? err : err ? new Error(String(err)) : null;
  events.emit('state', s);
}

export const getMicState = (): MicState => state;
export const getMicError = (): Error | null => error;
export const isMicOn = (): boolean => state === 'on';
/** Smoothed input level (RMS) for meters. */
export const getMicLevel = (): number => level;
export const isMicSupported = (): boolean =>
  typeof navigator !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia &&
  (typeof window === 'undefined' || window.isSecureContext !== false);

export const onMic = <K extends keyof MicEvents>(evt: K, fn: (data: MicEvents[K]) => void): (() => void) =>
  events.on(evt, fn);

/** Current level threshold: a margin over the measured noise floor, scaled by the sensitivity setting. */
export const micGate = (): number => baseGate(floor.value) / (getSettings().micSensitivity || 1);

export async function startMic(): Promise<boolean> {
  if (state === 'on') return true;
  if (!isMicSupported()) {
    setState('unavailable');
    return false;
  }
  setState('starting');
  try {
    const ctx = await resume();
    const audio: MediaTrackConstraints = {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      channelCount: 1,
    };
    const device = getSettings().micDevice;
    if (device) audio.deviceId = { exact: device };
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio });
    } catch (e) {
      const name = (e as DOMException)?.name;
      if (device && (name === 'OverconstrainedError' || name === 'NotFoundError')) {
        delete audio.deviceId;
        setSetting('micDevice', '');
        stream = await navigator.mediaDevices.getUserMedia({ audio });
      } else throw e;
    }
    src = ctx.createMediaStreamSource(stream);
    analyser = ctx.createAnalyser();
    analyser.fftSize = ctx.sampleRate > 60000 ? 32768 : 16384;
    analyser.smoothingTimeConstant = 0;
    sink = ctx.createGain();
    sink.gain.value = 0;
    src.connect(analyser);
    analyser.connect(sink);
    sink.connect(ctx.destination);
    buf = new Float32Array(analyser.fftSize);
    floor.restart();
    setState('on');
    award('mikrofon');
    lastT = 0;
    raf = requestAnimationFrame(loop);
    for (const t of stream.getAudioTracks()) t.addEventListener('ended', () => stopMic());
    return true;
  } catch (e) {
    console.warn('mic', e);
    const name = (e as DOMException)?.name;
    const denied = name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError';
    setState(denied ? 'denied' : name === 'NotFoundError' ? 'nodevice' : 'error', e);
    return false;
  }
}

export function stopMic(): void {
  cancelAnimationFrame(raf);
  raf = 0;
  if (stream) for (const t of stream.getTracks()) t.stop();
  try {
    src?.disconnect();
    analyser?.disconnect();
    sink?.disconnect();
  } catch {
    /* already disconnected */
  }
  stream = null;
  src = null;
  analyser = null;
  sink = null;
  level = 0;
  openLevel = 0;
  if (state !== 'off') setState('off');
}

export async function micDevices(): Promise<MediaDeviceInfo[]> {
  try {
    const list = await navigator.mediaDevices.enumerateDevices();
    return list.filter((d) => d.kind === 'audioinput');
  } catch {
    return [];
  }
}

function loop(ts: number): void {
  raf = requestAnimationFrame(loop);
  if (!analyser || !src) return;
  if (ts - lastT < 14) return;
  lastT = ts;
  const ctx = src.context as AudioContext;
  analyser.getFloatTimeDomainData(buf);
  const sr = ctx.sampleRate;
  const W = sr > 60000 ? 4096 : 2048;
  const win = buf.subarray(buf.length - W);
  const now = ctx.currentTime;
  const gated = !getSettings().headphones && now < getBusyUntil();
  const r = detectPitch(win, sr, { minF: 60, maxF: 1400 });
  const hf = hfLevel(buf, Math.round(sr / 60));
  level = Math.max(r.rms, level * 0.86);
  // the room level, never learned from the app's own sound or from what the user plays (see floor.ts)
  if (!gated) {
    floor.push(r.rms, { sensitivity: getSettings().micSensitivity || 1, hold: events.count('chord') > 0 });
    openLevel = r.rms;
  }
  const a4 = getSettings().a4 || 440;
  const midi = r.freq > 0 ? freqToMidi(r.freq, a4) : null;
  const gate = micGate();
  events.emit('frame', {
    t: now,
    rms: r.rms,
    freq: r.freq,
    clarity: r.clarity,
    hf,
    gated,
    midi,
    gate,
    floor: floor.value,
    voiced: !gated && r.freq > 0 && r.clarity >= 0.8 && r.rms >= gate,
    buf,
    sr,
  });
  if (events.count('chord') && now - lastChroma > 0.09) {
    lastChroma = now;
    const c = chroma(buf, sr, { a4 });
    const level4k = rms(buf, buf.length - 4096, buf.length);
    events.emit('chord', {
      t: now,
      chroma: c.chroma,
      bass: c.bass,
      rms: level4k,
      gated,
      active: !gated && level4k >= gate * 1.2,
    });
  }
}

/** Note events from the live stream. Returns an unsubscribe function. */
export function trackNotes(
  mode: TrackerMode,
  onNote: (ev: NoteEvent, frame: MicFrame) => void,
  opts: Partial<TrackerOptions> = {},
): () => void {
  const tr = new NoteTracker({ mode, a4: getSettings().a4 || 440, ...opts });
  // A string still ringing when listening starts (the last answer) is not a new note: a guitar note counts
  // from its pluck. After the app's own sound paused listening, the same goes for a string that rang on
  // through the pause; one clearly louder than before the pause was plucked during it and counts. When
  // listening begins during the pause, "before" is the last level heard before the app began to sound.
  const guitar = mode === 'guitar';
  if (guitar) tr.arm();
  let wasGated = false;
  let before = openLevel;
  return onMic('frame', (f) => {
    if (f.gated) {
      if (!wasGated) tr.reset();
      wasGated = true;
      return;
    }
    if (wasGated && guitar && f.rms < Math.max(before * 2, f.gate)) tr.arm();
    before = f.rms;
    wasGated = false;
    tr.o.gate = f.gate * (mode === 'voice' ? 1.4 : 1);
    const ev = tr.push(f);
    if (ev) onNote(ev, f);
  });
}

/** Ranks a vocabulary of chords against a chord frame. */
export const matchChordFrame = (c: ChordFrame, vocab: readonly ChordRef[]): ChordMatch[] =>
  matchChord(c.chroma, c.bass, vocab);
